/**
 * GraphQL Type Adapters
 * 
 * Converts GraphQL types (which use Maybe<T> = T | null | undefined) to our
 * internal types (which don't allow null for required fields).
 * 
 * This solves the type incompatibility between GraphQL's nullable fields
 * and our stricter internal type definitions.
 */

import type { Deals, Contacts } from "@/src/gql/graphql";
import type { Deal } from "./deals";
import { parseBigFloat } from "@wtc/ui/lib/currency";

/**
 * Convert GraphQL Deals type to our internal Deal type
 * Handles the Maybe<T> fields by filtering out null values
 */
export function adaptGraphQLDealToDeal(graphqlDeal: Deals): Deal {
  return {
    id: graphqlDeal.id,
    company_id: graphqlDeal.company_id ?? "",
    company_name: graphqlDeal.company_name ?? undefined,
    title: graphqlDeal.title ?? "",
    description: graphqlDeal.description ?? undefined,
    // BigFloat outputs are strings — coerce so the `number`-typed domain model is honest.
    revenue: parseBigFloat(graphqlDeal.revenue) ?? 0,
    currency: graphqlDeal.currency ?? "USD",
    stage: (graphqlDeal.stage ?? "lead") as Deal["stage"],
    close_date: graphqlDeal.close_date ?? undefined,
    close_probability: graphqlDeal.close_probability ?? 0,
    is_recurring: graphqlDeal.is_recurring ?? false,
    monthly_amount: parseBigFloat(graphqlDeal.monthly_amount) ?? undefined,
    is_committed: graphqlDeal.is_committed ?? false,
    assigned_users: (graphqlDeal.assigned_users?.filter((u): u is string => u != null) ?? undefined),
    engineer_ids: (graphqlDeal.engineer_ids?.filter((u): u is string => u != null) ?? undefined),
    tags: (graphqlDeal.tags?.filter((t): t is string => t != null) ?? undefined),
    notes: graphqlDeal.notes ?? undefined,
    last_activity_at: graphqlDeal.last_activity_at ?? new Date().toISOString(),
    last_communication_at: graphqlDeal.last_communication_at ?? undefined,
    created_at: graphqlDeal.created_at ?? new Date().toISOString(),
    updated_at: graphqlDeal.updated_at ?? new Date().toISOString(),
    created_by: graphqlDeal.created_by ?? undefined,
    won_at: graphqlDeal.won_at ?? undefined,
    lost_at: graphqlDeal.lost_at ?? undefined,
    cost: parseBigFloat(graphqlDeal.cost) ?? undefined,
    margin_percentage: parseBigFloat(graphqlDeal.margin_percentage) ?? undefined,
    estimated_hours: parseBigFloat(graphqlDeal.estimated_hours) ?? undefined,
    schedule_status: graphqlDeal.schedule_status ?? undefined,
    budget_status: graphqlDeal.budget_status ?? undefined,
    scope_status: graphqlDeal.scope_status ?? undefined,
    alignment_status: graphqlDeal.alignment_status ?? undefined,
  };
}

/**
 * Convert array of GraphQL Deals to our internal Deal type
 */
export function adaptGraphQLDealsArrayToDealArray(
  graphqlDeals: Deals[]
): Deal[] {
  return graphqlDeals.map(adaptGraphQLDealToDeal);
}

/**
 * Type guard to check if a value is a GraphQL Deals type
 */
export function isGraphQLDeal(value: unknown): value is Deals {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof (value as any).id === "string"
  );
}

/**
 * Type guard to check if a value is our internal Deal type
 */
export function isDeal(value: unknown): value is Deal {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "title" in value &&
    typeof (value as any).id === "string" &&
    typeof (value as any).title === "string"
  );
}
